/**
 * PromoPoster.js — Goosecross 12-day rotating promo + Day 13+ fallback.
 *
 * OVERRIDES: src/components/base/PromoPoster.js
 * REASON: Old Goose ran a dynamic "12 Days of Goosecross" calendar — each day
 *   showed a different banner image linking to a specific product page. After
 *   day 12 it fell back to a static "Weekend of Wine, Nature & Connection"
 *   promo with a Reserve Your Spot CTA.
 *
 * DAY MAPPING (script.js DaysBanners 1287-1348). The lookup key is the
 *   day-of-month in America/Los_Angeles, 1..12. Day 13+ → fallback.
 *
 * INTERFACE: render() → HTMLElement | null.
 *
 * Reference: goosecross/script.js Lines 1287-1348 (DaysBanners),
 *   1351-1354 (redirectToPromo), 1361-1426 (banner DOM).
 */

import { emit } from "../../../core/state.js";

const ASSET_BASE = "assets/goosecross/banners";

// Day-of-month (PST) → { image relative path, destination URL, label for emit }.
// Mirrors old DaysBanners; product slugs preserved verbatim so URLs still resolve.
const DAYS = {
  1:  { image: `${ASSET_BASE}/day-1.png`,  url: "https://www.goosecross.com/product/?view=product&slug=2022_HollysBlock_CabernetSauvignon",     label: "2022 Holly's Block Cabernet Sauvignon" },
  2:  { image: `${ASSET_BASE}/day-2.png`,  url: "https://www.goosecross.com/product?view=product&slug=2022_StateLane_Merlot",                    label: "2022 GooseCross State Lane Merlot" },
  3:  { image: `${ASSET_BASE}/day-3.png`,  url: "https://www.goosecross.com/product?view=product&slug=2022_StateLane_CabernetFranc",            label: "2022 GooseCross State Lane Cabernet Franc" },
  4:  { image: `${ASSET_BASE}/day-4.png`,  url: "https://www.goosecross.com/product?view=product&slug=2022_GooseCross_Branta",                  label: "2022 GooseCross Branta" },
  5:  { image: `${ASSET_BASE}/day-5.png`,  url: "https://www.goosecross.com/shop/?view=products&slug=merchandise",                              label: "Goosecross Merchandise" },
  6:  { image: `${ASSET_BASE}/day-6.png`,  url: "https://www.goosecross.com/product?view=product&slug=2022_GooseCross_Syrah",                   label: "2022 GooseCross Syrah" },
  7:  { image: `${ASSET_BASE}/day-7.png`,  url: "https://www.goosecross.com/product?view=product&slug=2022_PinotNoir",                          label: "2022 Pinot Noir" },
  8:  { image: `${ASSET_BASE}/day-8.png`,  url: "https://www.goosecross.com/product?view=product&slug=2022_StateLane_Malbec",                   label: "2022 State Lane Malbec" },
  9:  { image: `${ASSET_BASE}/day-9.png`,  url: "https://www.goosecross.com/product?view=product&slug=2024_Rose_for_Lucy",                      label: "2024 GooseCross A Rose for Lucy" },
  10: { image: `${ASSET_BASE}/day-10.png`, url: "https://www.goosecross.com/product/?view=product&slug=2022_LukeDavidson_Cabernet_Sauvignon",    label: "2022 GooseCross Coombsville Luke Davidson Cabernet Sauvignon" },
  11: { image: `${ASSET_BASE}/day-11.png`, url: "https://www.goosecross.com/shop/?view=products&slug=state-lane-subscription-box",              label: "State Lane Subscription Box" },
  12: { image: `${ASSET_BASE}/day-12.png`, url: "https://www.goosecross.com/shop/?view=products&slug=12-days-mystery-case",                     label: "12 Days Mystery Case" },
};

// After day 12, show the static "Weekend of Wine, Nature & Connection" promo.
const FALLBACK = {
  text: "A Weekend of Wine, Nature & Connection",
  ctaText: "Reserve Your Spot",
  url: "https://www.goosecross.com/visit",
};

// Returns the day-of-month in America/Los_Angeles, 1..31. No external date deps.
function getDayPST() {
  // Intl.DateTimeFormat resolves the tz on every call; cheap once per render.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    day: "numeric",
  }).formatToParts(new Date());
  const dayPart = parts.find((p) => p.type === "day");
  return dayPart ? parseInt(dayPart.value, 10) : NaN;
}

export default class PromoPoster {
  render() {
    const day = getDayPST();
    const entry = DAYS[day];

    const wrap = document.createElement("div");
    wrap.className = "ws-promo-poster ws-goose-promo";

    if (entry) {
      const img = document.createElement("img");
      img.className = "ws-promo-poster-img";
      img.src = entry.image;
      img.alt = entry.label;
      img.loading = "lazy";
      wrap.appendChild(img);

      wrap.classList.add("ws-clickable");
      wrap.style.cursor = "pointer";
      wrap.addEventListener("click", () => {
        emit("ws-promo-click", { poster: { image_url: entry.image, link_url: entry.url, alt: entry.label } });
        window.open(entry.url, "_blank", "noopener,noreferrer");
      });
      return wrap;
    }

    // Day 13+ fallback — static "Weekend of Wine" promo with Reserve CTA.
    wrap.classList.add("ws-goose-promo-fallback");

    const text = document.createElement("p");
    text.className = "ws-goose-promo-text";
    text.textContent = FALLBACK.text;
    wrap.appendChild(text);

    const cta = document.createElement("a");
    cta.className = "ws-card-cta ws-goose-promo-cta";
    cta.href = FALLBACK.url;
    cta.target = "_blank";
    cta.rel = "noopener noreferrer";
    cta.textContent = FALLBACK.ctaText;
    wrap.appendChild(cta);

    return wrap;
  }
}
